import { trackClientEvent } from 'analytics/analytics'
import CompareOffersToggle from 'components/Common/CompareOffersToggle'
import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import type { FilterOptionItems } from 'components/OfferList/OfferListFilter/OfferListFilterUtils'
import {
  generateOptionItems,
  hasNoAvailableFilters,
  optionMapToOptions,
  sortPopularItems,
  sortRecentItems,
} from 'components/OfferList/OfferListFilter/OfferListFilterUtils'
import MapWindow from 'components/Search/MapWindow'
import type { EnabledFilters, RecentFilters } from 'components/Search/type'
import {
  getAgentHubExclusiveFilterDisplayName,
  getAppliedFilterCount,
  getLuxPlusFilterDisplayName,
  getLoyaltyFilterDisplayName,
  getRecentFilters,
  isAgentHubExclusiveFilter,
  isOfTypeLoyaltyFeatureFilter,
  isOfTypeLuxPlusFeatureFilter,
  saveRecentFilters,
  getIsEnabledFilterKey,
} from 'components/Search/utils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { OFFER_TYPE_VILLA } from 'constants/offer'
import type { OfferListSortOption } from 'constants/offerListFilters'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import {
  ANYWHERE_PLACE_ID,
  CUSTOMER_RATING_TO_KEY,
  LIMITED_TIME_LUX_EXCLUSIVE,
} from 'constants/search'
import useCustomerRatingOptions from 'hooks/Search/useCustomerRatingOptions'
import { queryKeyFilters, queryKeyPages, chainsDelimiter } from 'constants/url'
import useOfferListFilters from 'hooks/Offers/useOfferListFilters'
import { EmptyArray, isNonEmptyArray, take, unique } from 'lib/array/arrayUtils'
import { isNumber } from 'lib/maths/mathUtils'
import getObjectKey from 'lib/object/getObjectKey'
import {
  deleteSearchParams,
  setManySearchParamValues,
  setSearchParamValue,
  toggleSearchParamValue,
} from 'lib/url/searchUrlUtils'
import debounce from 'lodash/debounce'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { rem } from 'polished'
import type { ChangeEventHandler } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addHistoricalAction } from 'actions/SystemActions'
import { selectLoggedIn } from 'selectors/accountSelectors'
import HotelSearchAddDestinationToggle from './HotelAddDestinationToggle/HotelSearchAddDestinationToggle'
import { NewBadge } from './HotelSearchFilterNewBadge'
import HotelSearchFilterPaneLoadingSkeleton from './HotelSearchFilterPaneLoadingSkeleton'
import RoomFilterGroup from './RoomFilterGroup'
import { LUX_PLUS_MONTH_CAMPAIGN_ID, LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { TakeoverId, isLabCampaignTagPayload, isLabSiteTakeoverPayload } from 'lib/lab/dynamicPayload'
import { SiteTakeoverCampaignTagLabel } from 'takeovers/utils/components/SiteTakeoverCampaignTagLabel'
import { LabFeatureFlags } from 'constants/lab'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import { LOYALTY_SEARCH_FILTER_PARAM } from 'loyalty/constants/loyaltySearchFilters'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import { updateRoomFilterSearch } from './utils/updateRoomFilterSearch'

type OfferListFilterType = Exclude<keyof App.OfferListAvailableFilters, 'total'>

interface FilterChangedParams {
  search: string
  hash?: string
}

export type FilterControlChangeHandler = ((params: FilterChangedParams) => void)

const newOptions = new Set(['Homes & Villas'])
const DEBOUNCE_DELAY = 500

const InputGroup = styled(Group)`
  margin-top: ${rem(16)};
`

const StyledBodyText = styled(BodyTextBlock)`
  margin-top: ${rem(8)};
`

const StyledAddDestinationToggle = styled(HotelSearchAddDestinationToggle)`
  border-bottom: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  padding-bottom: ${rem(8)};
`

const FilterPaneContainer = styled.div<{ $applyZoomCompensation?: boolean }>`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  padding-top: ${rem(20)};

  ${mediaQueryUp.desktop} {
    height: calc(var(--vh) * 100);
    padding-inline: 0;
  }

  ${props => props.$applyZoomCompensation && `
    min-height: 165vh;
  `}
`

const StickyHeader = styled(Group)`
  padding-bottom: ${rem(8)};
`

const ScrollableContent = styled.div`
  overflow-y: auto;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */

  ${mediaQueryUp.desktop} {
    /* To accommodate the offer comparison footer */
    padding-bottom: ${rem(100)};
  }
`

const MobileComparisonToggleWrapper = styled.div`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
  padding-block: ${rem(16)};
`

interface MappedStateProps {
  fetchingPopularFilters: boolean
  popularFilters: App.PopularFilters
  pathname: string
  currentHash: string
  canViewLuxPlusBenefits: boolean
  userId?: string
  isLoggedIn: boolean
}

interface Props {
  filters: App.OfferListFilters
  mapViewURL: string
  search: string
  enabledFilters: EnabledFilters
  fetching?: boolean
  mainListFetching?: boolean
  sortOptions?: Array<OfferListSortOption>
  hotelOfferList?: App.OfferList
  overrideAvailableFilters?: App.OfferListFilterOptions
  doNotScrollToTopOfSelection?: boolean
  showLoadingSkeletonOnEachFilterSelection?: boolean
  bedroomsFilterGroupAtBottom?: boolean
  onFilterChange: FilterControlChangeHandler
  onClose?: () => void
  hideCompareOffersToggle?: boolean
  hideAddDestinationToggle?: boolean
  showNoFiltersAvailable?: boolean
  applyZoomCompensation?: boolean
  showRoomsAndAmenitiesAtTop?: boolean
}

type Option = [string, number, number]

// Custom hook to track filter changes and dispatch historical actions
// Tracks "ever selected in session" and current active state
// Initialize to empty so URL filters are treated as "new" on first render (rehydration)
function useFilterChangeTracking(
  filterType: 'inclusions' | 'amenities',
  currentFilters: Array<string> | undefined,
  dispatch: ReturnType<typeof useAppDispatch>,
) {
  const previousFiltersRef = useRef<Array<string>>(EmptyArray)

  useEffect(() => {
    const previousFilters = previousFiltersRef.current
    const currentFiltersArray = currentFilters ?? EmptyArray

    // Find newly checked filters
    const newlyCheckedFilters = currentFiltersArray.filter(
      filter => !previousFilters.includes(filter),
    )

    // Find newly unchecked filters
    const newlyUncheckedFilters = previousFilters.filter(
      filter => !currentFiltersArray.includes(filter),
    )

    // Dispatch active: true for newly checked
    newlyCheckedFilters.forEach((filter) => {
      dispatch(addHistoricalAction('filter_selected', {
        filterType,
        value: filter,
        active: true,
      }))
    })

    // Dispatch active: false for newly unchecked
    newlyUncheckedFilters.forEach((filter) => {
      dispatch(addHistoricalAction('filter_selected', {
        filterType,
        value: filter,
        active: false,
      }))
    })

    previousFiltersRef.current = currentFiltersArray
  }, [currentFilters, dispatch, filterType])
}

function HotelSearchPageFilterPane(props: Props & MappedStateProps) {
  const {
    filters,
    fetching,
    mapViewURL,
    sortOptions = EmptyArray,
    search,
    enabledFilters,
    popularFilters,
    fetchingPopularFilters,
    pathname,
    currentHash,
    onFilterChange,
    onClose,
    canViewLuxPlusBenefits,
    isLoggedIn,
    userId,
    overrideAvailableFilters,
    mainListFetching,
    doNotScrollToTopOfSelection = false,
    showLoadingSkeletonOnEachFilterSelection = false,
    bedroomsFilterGroupAtBottom = false,
    hideCompareOffersToggle = false,
    hideAddDestinationToggle = false,
    showNoFiltersAvailable = false,
    applyZoomCompensation = false,
    showRoomsAndAmenitiesAtTop = false,
  } = props
  const scrollableContentRef = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const { SORT_OPTION_RECOMMENDED } = useOfferListSortOptions()
  const { CUSTOMER_RATING_GTE, CUSTOMER_RATING_GTE_BY_VALUE } = useCustomerRatingOptions()

  const filtersHeading = intl.formatMessage({
    id: 'oOtxgVWtqoiio1YzzqkB',
    defaultMessage: 'Filters',
    description: 'Heading for the filter pane on search page in the search interface',
  })
  const sortAndFiltersHeading = intl.formatMessage({
    id: 'NN2kutDlwidQq_pNtcfy',
    defaultMessage: 'Sort & filters',
    description: 'Mobile heading for sort and filters pane on search page',
  })
  const sortByTitle = intl.formatMessage({
    id: 'gmh0miF1EG1G1HaHJx8K',
    defaultMessage: 'Sort by',
    description: 'Title for sort options group on search page in the search interface',
  })
  const noFiltersAvailableText = intl.formatMessage({
    id: 'HUzmPYnG8Qs-S8m8iKC8',
    defaultMessage: 'No filters available',
    description: 'Message shown when no filters are available on search page',
  })
  const recentFiltersTitle = intl.formatMessage({
    id: 'V_cvHNSqCNqYCnh235p5',
    defaultMessage: 'Recent filters',
    description: 'Heading title for recent filters group on the search page',
  })
  const popularFiltersTitle = intl.formatMessage({
    id: 'NAsQyB5EhYsAKCEk3iRE',
    defaultMessage: 'Popular filters',
    description: 'Heading title for popular filters group on the search page',
  })
  const promotionsTitle = intl.formatMessage({
    id: 's8xf7jzKEKgIMLw8kIsb',
    defaultMessage: 'Promotions',
    description: 'Heading title for promotions filter group on the search page',
  })
  const customerRatingTitle = intl.formatMessage({
    id: 'Kl7pgMk-5JhbPOyy7E4e',
    defaultMessage: 'Customer rating',
    description: 'Title for customer rating filter group on search page',
  })
  const holidayTypeTitle = intl.formatMessage(
    {
      id: 'BGh1bo8iEvzmOHxc6Pv-',
      defaultMessage: 'Type of {holidaySynonym}',
      description: 'Title for holiday type filter group on search page',
    },
    { holidaySynonym: config.brandConfig.holidaySynonym },
  )
  const inclusionsTitle = intl.formatMessage({
    id: 'MRkUK8Yn4LEC6Ws_tkF7',
    defaultMessage: 'Inclusions',
    description: 'Heading title for inclusions filter group on the search page',
  })
  const amenitiesTitle = intl.formatMessage({
    id: 'YornE0030EkWkqAvYhbw',
    defaultMessage: 'Amenities',
    description: 'Heading title for amenities filter group on the search page',
  })
  const paymentOptionsTitle = intl.formatMessage({
    id: 'TrTAW1ZiSr8-mYCZMutT',
    defaultMessage: 'Payment options',
    description: 'Title for payment options filter group on search page',
  })
  const propertyBrandsTitle = intl.formatMessage({
    id: 'Bti3XMtVrJLnG0LpoetS',
    defaultMessage: 'Property brands',
    description: 'Title for property brands filter group on search page',
  })
  const agentHubFeaturesTitle = intl.formatMessage(
    {
      id: 'mpE2YhOBk81tdtDXN2K7',
      defaultMessage: '{displayName} Features',
      description: 'Title for agent hub features filter group on search page',
    },
    { displayName: agentHubOrSubBrandConfig.displayShortName },
  )
  const closeLabel = intl.formatMessage({
    id: 'M8NWF3WiZsj9O3wRx3Lm',
    defaultMessage: 'close',
    description: 'Aria label for close button on filter pane in the filters panel',
  })

  const searchParams = useMemo(() => {
    const params = new URLSearchParams(search)
    params.delete(queryKeyPages)
    return params.toString()
  }, [search])

  const [localAvailableFilters, setLocalAvailableFilters] = useState<App.OfferListFilterOptions | undefined>()
  const recentFilters = getRecentFilters()
  const getFilterDisplayName = useCallback((key: string, filter: string): string => {
    if (key === 'luxPlusFeatures') {
      return getLuxPlusFilterDisplayName(intl, filter)
    }

    if (key === 'loyalty') {
      return getLoyaltyFilterDisplayName(intl, filter)
    }

    if (key === 'agentHubFeatures') {
      return getAgentHubExclusiveFilterDisplayName(intl, filter)
    }

    return filter
  }, [intl])
  const isMapPage = pathname.includes('/map')
  const fetchedAvailableFilters = useOfferListFilters(filters)
  const availableFilters = overrideAvailableFilters ?? fetchedAvailableFilters

  const currentTakeover = useCurrentSiteTakeover()
  const isLPMonthTakeoverEnabled = currentTakeover?.id === LUX_PLUS_MONTH_TAKEOVER_ID

  const siteTakeoverFeatureFlag = useLabFeatureFlag(LabFeatureFlags.enableSiteTakeover)
  const isBestOf2025Enabled = useMemo(() => {
    if (!siteTakeoverFeatureFlag?.enabled) {
      return false
    }
    const payload = siteTakeoverFeatureFlag.value
    return isLabSiteTakeoverPayload(payload) && payload.id === TakeoverId.BEST_OF_2025_TAKEOVER
  }, [siteTakeoverFeatureFlag])

  const campaignTagFeatureFlag = useLabFeatureFlag(LabFeatureFlags.enableOfferCampaignTag)
  const bestOf2025CampaignInfo = useMemo(() => {
    if (!isBestOf2025Enabled || !campaignTagFeatureFlag?.enabled) {
      return undefined
    }
    const payload = campaignTagFeatureFlag.value
    if (isLabCampaignTagPayload(payload)) {
      return {
        campaignId: payload.campaignId,
        campaignTag: payload.campaignTag,
      }
    }
    return undefined
  }, [isBestOf2025Enabled, campaignTagFeatureFlag])

  useEffect(() => {
    if (!showLoadingSkeletonOnEachFilterSelection) {
      setLocalAvailableFilters(availableFilters)
      return
    }

    // do not set the new filters until they have finished loading
    if (!availableFilters.fetching && !mainListFetching) {
      setLocalAvailableFilters(availableFilters)
    }
  }, [availableFilters, filters.propertyId, mainListFetching, showLoadingSkeletonOnEachFilterSelection])

  const topLevelSearchKey = getObjectKey({
    propertyId: filters.propertyId,
    destinationId: filters.destinationId,
    landmarkId: filters.landmarkId,
    checkIn: filters.checkIn,
    checkOut: filters.checkOut,
    flexibleMonths: filters.flexibleMonths,
    flexibleNights: filters.flexibleNights,
    rooms: filters.rooms,
    query: filters.query,
  })

  // Reset existing filters when top-level search parameters change (destination, dates, rooms)
  useEffect(() => {
    if (!showLoadingSkeletonOnEachFilterSelection) return

    if (availableFilters.fetching) {
      setLocalAvailableFilters(undefined)
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [topLevelSearchKey])

  const isAnywhereSearch = filters.destinationId === ANYWHERE_PLACE_ID
  const showLoadingSkeletons = showLoadingSkeletonOnEachFilterSelection ? !localAvailableFilters : availableFilters.fetching
  const sortByChecked = true
  const hideCounts = true

  const customerRatingItems = useMemo(
    () => {
      const items = [
        {
          value: CUSTOMER_RATING_GTE.EXCEPTIONAL.value,
          label: <Group direction="horizontal" horizontalAlign="space-between">
            {CUSTOMER_RATING_GTE.EXCEPTIONAL.label}
          </Group>,
          checked: filters?.customerRatingGte === CUSTOMER_RATING_GTE.EXCEPTIONAL.value,
          count: localAvailableFilters?.filters?.customerRatings?.Exceptional,
          hideCount: hideCounts,
        },
        {
          value: CUSTOMER_RATING_GTE.VERY_GOOD.value,
          label: <Group direction="horizontal" horizontalAlign="space-between">
            {CUSTOMER_RATING_GTE.VERY_GOOD.label}
          </Group>,
          checked: filters?.customerRatingGte === CUSTOMER_RATING_GTE.VERY_GOOD.value,
          count: localAvailableFilters?.filters?.customerRatings?.['Very Good'],
          hideCount: hideCounts,
        },
        {
          value: CUSTOMER_RATING_GTE.GOOD.value,
          label: <Group direction="horizontal" horizontalAlign="space-between">
            {CUSTOMER_RATING_GTE.GOOD.label}
          </Group>,
          checked: filters?.customerRatingGte === CUSTOMER_RATING_GTE.GOOD.value,
          count: localAvailableFilters?.filters?.customerRatings?.Good,
          hideCount: hideCounts,
        },
      ]
      return items.filter((item) => item.count && item.count > 0)
    }, [localAvailableFilters?.filters?.customerRatings, filters?.customerRatingGte, hideCounts, CUSTOMER_RATING_GTE])

  const { holidayTypeItems, amenityItems, inclusionItems, luxPlusFeatureItems, loyaltyFeatureItems, agentHubFeatureItems, paymentOptionItems, promotionItems, chainItems } = useMemo(() => {
    const luxPlusFilters = localAvailableFilters?.filters?.luxPlusFeatures ?? {}
    if (!canViewLuxPlusBenefits && 'Member exclusive' in luxPlusFilters) {
      delete luxPlusFilters['Member exclusive']
    }
    const holidayTypes = optionMapToOptions(localAvailableFilters?.filters?.holidayTypes ?? {}, localAvailableFilters?.filterOrder?.holidayTypes ?? {})
    const amenities = optionMapToOptions(localAvailableFilters?.filters?.amenities ?? {}, localAvailableFilters?.filterOrder?.amenities ?? {})
    const paymentOptions = optionMapToOptions(localAvailableFilters?.filters?.paymentOptions ?? {}, {})
    const chainsFilters = localAvailableFilters?.filters?.chains ?? {}
    const chains = optionMapToOptions(chainsFilters, chainsFilters)
    const luxPlusFeatures = optionMapToOptions(luxPlusFilters ?? {}, {})
    const agentHubFeatures = optionMapToOptions(localAvailableFilters?.filters?.agentHubFeatures ?? {}, {})
    const inclusions = Object.entries(localAvailableFilters?.filters?.inclusions ?? {}).reduce((acc, [category, inclusions]) => {
      acc[category] = optionMapToOptions(inclusions ?? {}, {})
      return acc
    }, {} as Record<string, Array<Option>>)

    const inclusionItems = Object.entries(inclusions).reduce((acc, [category, inclusionOptions]) => {
      acc[category] = generateOptionItems(inclusionOptions, new Set(filters.inclusions), { sortByChecked, hideCounts })
      return acc
    }, {} as Record<string, Array<FilterPanelCheckItem>>)
    const campaignFeatureItems: Array<FilterOptionItems> = []
    if (isLPMonthTakeoverEnabled && localAvailableFilters?.filters?.campaigns[LUX_PLUS_MONTH_CAMPAIGN_ID]) {
      campaignFeatureItems.push({
        label: <div>{intl.formatMessage({
          id: '5NBFlWtaFGFXj2r__WrX',
          defaultMessage: 'LuxPlus+ month',
          description: 'Filter label for LuxPlus+ month campaign on hotel search page',
        })}
        </div>,
        value: LUX_PLUS_MONTH_CAMPAIGN_ID,
        checked: !!filters.campaigns?.includes(LUX_PLUS_MONTH_CAMPAIGN_ID),
        count: localAvailableFilters?.filters?.campaigns[LUX_PLUS_MONTH_CAMPAIGN_ID],
        hideCount: hideCounts,
      })
    }

    const loyaltyOptions = optionMapToOptions(localAvailableFilters?.filters?.loyalty ?? {}, {})
    // Apply display name mapping for loyalty filters
    const loyaltyFeatureItems = generateOptionItems(loyaltyOptions, new Set(filters.loyalty), { sortByChecked, hideCounts }).map(item => ({
      ...item,
      label: <div>
        <span>{getLoyaltyFilterDisplayName(intl, item.value as string)}</span>
      </div>,
    }))

    const promotionItems: Array<FilterOptionItems> = []
    if (isBestOf2025Enabled && bestOf2025CampaignInfo) {
      const campaignCount = localAvailableFilters?.filters?.campaigns?.[bestOf2025CampaignInfo.campaignId]
      promotionItems.push({
        label: <SiteTakeoverCampaignTagLabel campaignTagId={bestOf2025CampaignInfo.campaignId} forcedVisible />,
        value: bestOf2025CampaignInfo.campaignId,
        checked: !!filters.campaigns?.includes(bestOf2025CampaignInfo.campaignId),
        count: campaignCount,
        hideCount: hideCounts,
      })
    }

    return {
      holidayTypeItems: generateOptionItems(holidayTypes, new Set(filters.holidayTypes), { sortByChecked, hideCounts, newOptions }),
      amenityItems: generateOptionItems(amenities, new Set(filters.amenities), { sortByChecked, hideCounts }),
      luxPlusFeatureItems: [
        // Temporary fix for LuxPlus+ month campaign
        ...campaignFeatureItems,
        ...generateOptionItems(luxPlusFeatures, new Set(filters.luxPlusFeatures), { sortByChecked, hideCounts })],
      loyaltyFeatureItems,
      inclusionItems,
      campaignFeatureItems: campaignFeatureItems,
      agentHubFeatureItems: generateOptionItems(agentHubFeatures, new Set(filters.agentHubFeatures), { sortByChecked, hideCounts }),
      paymentOptionItems: generateOptionItems(paymentOptions, new Set(filters.paymentOptions), { sortByChecked, hideCounts }),
      chainItems: generateOptionItems(chains, new Set(filters.chains), { sortByChecked, hideCounts }),
      promotionItems,
    }
  }, [localAvailableFilters, canViewLuxPlusBenefits, filters, isLPMonthTakeoverEnabled, isBestOf2025Enabled, bestOf2025CampaignInfo, sortByChecked, hideCounts, intl])

  // Track filter changes for inclusions and amenities
  useFilterChangeTracking('inclusions', filters.inclusions, dispatch)
  useFilterChangeTracking('amenities', filters.amenities, dispatch)

  const itemifyFilterGroup = useCallback((filterGroup: App.PopularFilters | RecentFilters) => {
    const flatInclusions: Record<string, number> = {}

    if (localAvailableFilters?.filters?.inclusions) {
      Object.values(localAvailableFilters.filters.inclusions ?? {}).forEach(record => {
        for (const key in record) {
          flatInclusions[key] = record[key]
        }
      })
    }

    const isFilterChecked = (filterGroupKey: string, filter: string) => {
      if (filterGroupKey in filters) {
        const filterGroup = filters[filterGroupKey as keyof App.OfferListFilters]
        if (Array.isArray(filterGroup) && filterGroup.length > 0) {
          return (filterGroup as Array<string>).includes(filter)
        }
      }
      return false
    }

    const items = Object.entries(filterGroup).map(([key, value]: [string, Array<string> | number]) => {
      if (key === 'customerRatingGte' && 'customerRatingGte' in filterGroup && isNumber(value)) {
        const customerRating = filterGroup.customerRatingGte ? CUSTOMER_RATING_GTE_BY_VALUE[filterGroup.customerRatingGte] : undefined
        return {
          value,
          label: customerRating?.label,
          count: localAvailableFilters?.filters?.customerRatings?.[CUSTOMER_RATING_TO_KEY[value]],
          checked: filters.customerRatingGte === value,
          enabled: enabledFilters.customerRating,
          hideCount: hideCounts,
        }
      }
      if (isNumber(value) || key === 'orderBy' || (key === 'locations' && !isAnywhereSearch)) {
        return
      }
      return unique(value).map((filter) => ({
        value: filter,
        label: <div>
          <span>{getFilterDisplayName(key, filter)}</span>
          {newOptions.has(filter) && <NewBadge>{intl.formatMessage({
            id: 'mZLAt9872vTo1eXKxeP5',
            defaultMessage: 'NEW',
            description: 'Badge label for new filter options on hotel search page',
          })}
          </NewBadge>}
        </div>,
        count: key === 'inclusions' ? flatInclusions[filter] : (localAvailableFilters?.filters?.[key as OfferListFilterType] as { [key: string]: number })?.[filter],
        checked: isFilterChecked(key, filter),
        enabled: getIsEnabledFilterKey(key) && enabledFilters[key],
        hideCount: hideCounts,
      }))
    }).flat()
    return items.filter(Boolean) as Array<FilterPanelCheckItem>
  }, [filters, isAnywhereSearch, localAvailableFilters, enabledFilters, hideCounts, intl, CUSTOMER_RATING_GTE_BY_VALUE, getFilterDisplayName])

  const recentFilterItems = useMemo(() => {
    const filteredSortedRecentItems = itemifyFilterGroup(recentFilters)
      .filter(item => item?.enabled && ((item.count && item.count > 0) || item.checked))
      .sort((a, b) => sortRecentItems(a, b, recentFilters?.orderBy ?? []))
    return take(filteredSortedRecentItems, 10)
  }, [recentFilters, itemifyFilterGroup])

  const popularFilterItems = useMemo(() => {
    const filteredSortedPopularItems = itemifyFilterGroup(popularFilters)
      .filter(item => item?.count && item.count > 0)
      .sort((a, b) => sortPopularItems(a, b, popularFilters.orderBy, newOptions))

    if (isMapPage && localAvailableFilters?.filters?.holidayTypes[LIMITED_TIME_LUX_EXCLUSIVE]) {
      filteredSortedPopularItems.unshift({
        value: LIMITED_TIME_LUX_EXCLUSIVE,
        label: intl.formatMessage({
          id: 'qoNMyluzc6oCyzKIvJPT',
          defaultMessage: 'Limited Time LUX Exclusive',
          description: 'Popular filter label for limited time LUX exclusive holiday type',
        }),
        count: localAvailableFilters?.filters?.holidayTypes[LIMITED_TIME_LUX_EXCLUSIVE],
        checked: filters.holidayTypes?.includes(LIMITED_TIME_LUX_EXCLUSIVE),
        hideCount: hideCounts,
      })
    }
    return take(filteredSortedPopularItems, 10)
  }, [itemifyFilterGroup, popularFilters, isMapPage, localAvailableFilters?.filters?.holidayTypes, filters.holidayTypes, hideCounts, intl])

  const scrollToTop = useCallback(() => {
    if (doNotScrollToTopOfSelection) return

    // Scroll the scrollable content and the page to to top
    scrollableContentRef.current?.scrollTo({
      top: 0,
      behavior: 'instant',
    })
    window.scrollTo({
      top: 0,
      behavior: 'instant' as any,
    })
  }, [doNotScrollToTopOfSelection])

  const onLuxPlusFeatureChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === LUX_PLUS_MONTH_CAMPAIGN_ID) {
      onFilterChange({ search: toggleSearchParamValue(searchParams, 'campaigns', value), hash: currentHash })
      saveRecentFilters({ luxPlusFeatures: [value] })
    } else
      if (value.length && value.split(',').length === 1) {
        onFilterChange({ search: toggleSearchParamValue(searchParams, 'luxPlusFeatures', value), hash: currentHash })
        saveRecentFilters({ luxPlusFeatures: [value] })
      } else {
        onFilterChange({
          search: setManySearchParamValues(searchParams, [{ paramName: 'luxPlusFeatures', paramValue: value.length ? value : undefined }]),
          hash: currentHash,
        })
      }
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onPromotionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === bestOf2025CampaignInfo?.campaignId) {
      onFilterChange({ search: toggleSearchParamValue(searchParams, 'campaigns', value), hash: currentHash })
    }
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange, bestOf2025CampaignInfo])

  const onLoyaltyFeatureChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    const value = event.target.value
    const newSearchParams = toggleSearchParamValue(searchParams, LOYALTY_SEARCH_FILTER_PARAM, value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ loyalty: [value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onAgentHubFeatureChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const newSearchParams = toggleSearchParamValue(searchParams, 'agentHubFeatures', value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ agentHubFeatures: [value as App.AgentHubFeatureFilter] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onHolidayTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchParams = toggleSearchParamValue(searchParams, 'holidayTypes', e.target.value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ holidayTypes: [e.target.value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onAmenityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchParams = toggleSearchParamValue(searchParams, 'amenities', e.target.value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ amenities: [e.target.value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onPaymentOptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchParams = toggleSearchParamValue(searchParams, 'paymentOptions', e.target.value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ paymentOptions: [e.target.value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onChainChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchParams = toggleSearchParamValue(searchParams, 'chains', e.target.value, chainsDelimiter)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ chains: [e.target.value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onInclusionsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // historicalActions sync is handled by useEffect watching filters.inclusions
    const newSearchParams = toggleSearchParamValue(searchParams, 'inclusions', e.target.value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ inclusions: [e.target.value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onCustomerRatingChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (filters?.customerRatingGte === Number(e.target.value)) {
      onFilterChange({ search: deleteSearchParams(searchParams, 'customerRatingGte'), hash: currentHash })
    } else {
      onFilterChange({ search: setSearchParamValue(searchParams, 'customerRatingGte', e.target.value), hash: currentHash })
      saveRecentFilters({ customerRatingGte: Number(e.target.value) })
    }
    scrollToTop()
  }, [filters?.customerRatingGte, searchParams, currentHash, scrollToTop, onFilterChange])

  const onBedroomsChange = useCallback((value: number) => {
    onFilterChange({ search: updateRoomFilterSearch(searchParams, 'bedrooms', value), hash: currentHash })
    scrollToTop()
  }, [currentHash, searchParams, scrollToTop, onFilterChange])
  const debouncedBedroomsChange = useMemo(() => debounce(onBedroomsChange, DEBOUNCE_DELAY), [onBedroomsChange])

  const onBedsChange = useCallback((value: number) => {
    onFilterChange({ search: updateRoomFilterSearch(searchParams, 'beds', value), hash: currentHash })
    scrollToTop()
  }, [currentHash, searchParams, scrollToTop, onFilterChange])
  const debouncedBedsChange = useMemo(() => debounce(onBedsChange, DEBOUNCE_DELAY), [onBedsChange])

  const onBathroomsChange = useCallback((value: number) => {
    onFilterChange({ search: updateRoomFilterSearch(searchParams, 'bathrooms', value), hash: currentHash })
    scrollToTop()
  }, [currentHash, searchParams, scrollToTop, onFilterChange])
  const debouncedBathroomsChange = useMemo(() => debounce(onBathroomsChange, DEBOUNCE_DELAY), [onBathroomsChange])

  const onSortChange = useCallback((value: string) => {
    if (value === SORT_OPTION_RECOMMENDED.value) {
      onFilterChange({ search: deleteSearchParams(searchParams, 'sortBy'), hash: currentHash })
    } else {
      onFilterChange({ search: setSearchParamValue(searchParams, 'sortBy', value), hash: currentHash })
    }
    scrollToTop()
  }, [currentHash, searchParams, scrollToTop, onFilterChange, SORT_OPTION_RECOMMENDED])

  const onFilterGroupChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Find which filter group it belongs to then change the appropriate key in search
    const deleteParamsSearchParams = deleteSearchParams(searchParams, ...['propertyName', 'propertyId'])
    onFilterChange({ search: deleteParamsSearchParams, hash: currentHash })

    const value = e.target.value
    let filterGroup: string | undefined
    if (value === 'villa') {
      filterGroup = 'offerType'
    } else if (isAgentHubExclusiveFilter(value)) {
      filterGroup = 'agentHubFeatures'
    } else if ((localAvailableFilters?.filters?.holidayTypes && value in localAvailableFilters.filters.holidayTypes) || filters.holidayTypes?.includes(value)) {
      filterGroup = 'holidayTypes'
    } else if ((localAvailableFilters?.filters?.amenities && value in localAvailableFilters.filters.amenities) || filters.amenities?.includes(value)) {
      filterGroup = 'amenities'
    } else if (!isNaN(parseInt(value))) {
      filterGroup = 'customerRatingGte'
    } else if (filters.inclusions?.includes(value)) {
      filterGroup = 'inclusions'
    } else if (isOfTypeLuxPlusFeatureFilter(value)) {
      filterGroup = 'luxPlusFeatures'
    } else if (isOfTypeLoyaltyFeatureFilter(value)) {
      filterGroup = LOYALTY_SEARCH_FILTER_PARAM
    } else if ((localAvailableFilters?.filters?.paymentOptions && value in localAvailableFilters.filters.paymentOptions) || filters.paymentOptions?.includes(value)) {
      filterGroup = 'paymentOptions'
    } else if ((localAvailableFilters?.filters?.chains && value in localAvailableFilters.filters.chains) || filters.chains?.includes(value)) {
      filterGroup = 'chains'
    } else if (localAvailableFilters?.filters?.inclusions) {
      for (const key in localAvailableFilters.filters.inclusions) {
        if (value in localAvailableFilters.filters.inclusions[key]) {
          filterGroup = 'inclusions'
          break
        }
      }
    }
    if (!filterGroup) return
    if (filterGroup !== 'offerType') {
      saveRecentFilters({ [filterGroup]: filterGroup === 'customerRatingGte' ? Number(value) : [value] })
    }
    const delimiter = filterGroup === 'chains' ? chainsDelimiter : ','
    const newSearchParams = toggleSearchParamValue(searchParams, filterGroup, filterGroup === 'offerType' ? OFFER_TYPE_VILLA : value, delimiter)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    scrollToTop()
  }, [searchParams, localAvailableFilters?.filters, filters, currentHash, scrollToTop, onFilterChange])

  const onResetAllClick = useCallback(() => {
    const filtersToDelete = [...queryKeyFilters]
    const newSearchParams = deleteSearchParams(
      searchParams,
      ...filtersToDelete,
    )
    onFilterChange({ search: newSearchParams, hash: currentHash })
    scrollToTop()
  }, [currentHash, searchParams, scrollToTop, onFilterChange])

  const handleMapClick = useCallback(() => {
    trackClientEvent({
      subject: 'search_view_on_map',
      action: 'clicked',
      type: 'interaction',
      category: 'vertical_page',
    })
  }, [])

  const filterCount = useMemo(() => {
    return getAppliedFilterCount(filters, enabledFilters, hideCompareOffersToggle)
  }, [filters, enabledFilters, hideCompareOffersToggle])
  const hasFiltersApplied = filterCount > 0
  const resetAllLabel = intl.formatMessage(
    {
      id: 'SBUXnNFAtohL80iOyI2c',
      defaultMessage: 'Reset all ({filterCount})',
      description: 'Reset all filters link with active filter count on search page',
    },
    { filterCount },
  )

  const noAvailableFilters = localAvailableFilters && !localAvailableFilters.fetching && hasNoAvailableFilters(localAvailableFilters?.filters)
  const showRecentFilters = isNonEmptyArray(recentFilterItems)
  const showPopularFilters = enabledFilters.popularFilters && isNonEmptyArray(popularFilterItems) && !fetchingPopularFilters && !showRecentFilters

  const hasRoomBathOrBedFilters = useMemo(() => {
    return !!(enabledFilters.bedrooms || enabledFilters.bathrooms || enabledFilters.beds)
  }, [enabledFilters.bathrooms, enabledFilters.bedrooms, enabledFilters.beds])
  const shouldShowTopRoomsAndAmenities = showRoomsAndAmenitiesAtTop && hasRoomBathOrBedFilters && !bedroomsFilterGroupAtBottom

  const isLuxuryEscapes = config.BRAND === 'luxuryescapes'

  return (
    <FilterPaneContainer $applyZoomCompensation={applyZoomCompensation}>
      <StickyHeader direction="vertical" gap={32}>
        {mapViewURL && <MapWindow mapViewURL={mapViewURL} onClick={handleMapClick} />}
        <CSSBreakpoint min="tablet">
          <Group direction="vertical" gap={24}>
            <Group direction="vertical" gap={8}>
              {isLuxuryEscapes && filters.destinationId && filters.destinationId !== ANYWHERE_PLACE_ID && isLoggedIn && userId && !hideAddDestinationToggle &&
                <StyledAddDestinationToggle
                  userId={userId}
                  placeId={filters.destinationId}
                />}
              {config.SEARCH_OFFER_COMPARISON_ENABLED && !hideCompareOffersToggle && <CompareOffersToggle />}
            </Group>
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
              <Heading variant="heading4">{filtersHeading}</Heading>
              {hasFiltersApplied && <TextLink onClick={onResetAllClick}>{resetAllLabel}</TextLink>}
            </Group>
          </Group>
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
            <Heading variant="heading4">{sortAndFiltersHeading}</Heading>
            <IconButton kind="tertiary" variant="dark" horizontalOutdent="end" onClick={onClose} aria-label={closeLabel}>
              <LineTimesIcon />
            </IconButton>
          </Group>
          <MobileComparisonToggleWrapper>
            {config.SEARCH_OFFER_COMPARISON_ENABLED && !hideCompareOffersToggle && <CompareOffersToggle />}
          </MobileComparisonToggleWrapper>
        </CSSBreakpoint>
      </StickyHeader>
      {sortOptions.length > 0 && <CSSBreakpoint max="mobile">
        <FilterPanelFilterGroup title={sortByTitle}>
          <InputGroup direction="vertical" gap={12}>
            {sortOptions.map(sortOption => <RadioInput
              key={`sortOption_${sortOption.value}`}
              name="sortBy"
              value={sortOption.value}
              onChange={() => onSortChange(sortOption.value)}
              checked={sortOption.value === (filters?.sortBy ?? SORT_OPTION_RECOMMENDED.value)}
            >
              {sortOption.label}
            </RadioInput>)}
          </InputGroup>
        </FilterPanelFilterGroup>
      </CSSBreakpoint>}
      {showLoadingSkeletons && <HotelSearchFilterPaneLoadingSkeleton />}
      <ScrollableContent ref={scrollableContentRef}>
        {(noAvailableFilters || showNoFiltersAvailable) && <StyledBodyText variant="medium">{noFiltersAvailableText}</StyledBodyText>}
        {showRecentFilters && <FilterPanelFilterGroup title={recentFiltersTitle}>
          <FilterPanelCheckboxGroup
            name="recentFilters"
            items={recentFilterItems}
            onChange={onFilterGroupChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {shouldShowTopRoomsAndAmenities && (
          <RoomFilterGroup
            loading={fetching}
            filters={filters}
            enabledFilters={enabledFilters}
            onBedroomsChange={debouncedBedroomsChange}
            onBedsChange={debouncedBedsChange}
            onBathroomsChange={debouncedBathroomsChange}
          />
        )}
        {shouldShowTopRoomsAndAmenities && enabledFilters.amenities && isNonEmptyArray(amenityItems) && <FilterPanelFilterGroup loading={fetching} title={amenitiesTitle}>
          <FilterPanelCheckboxGroup
            name="amenities"
            items={amenityItems}
            onChange={onAmenityChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {showPopularFilters && <FilterPanelFilterGroup title={popularFiltersTitle}>
          <FilterPanelCheckboxGroup
            name="popularFilters"
            items={popularFilterItems}
            onChange={onFilterGroupChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {isBestOf2025Enabled && promotionItems.length > 0 && <FilterPanelFilterGroup loading={fetching} title={promotionsTitle}>
          <FilterPanelCheckboxGroup
            name="promotions"
            items={promotionItems}
            onChange={onPromotionChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}

        {enabledFilters.luxPlusFeatures && luxPlusFeatureItems.length > 0 && <FilterPanelFilterGroup loading={fetching} title={<LuxPlusLabel type="only-logo" />}>
          <FilterPanelCheckboxGroup
            name="luxPlusFeatures"
            items={luxPlusFeatureItems}
            onChange={onLuxPlusFeatureChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {enabledFilters.loyalty && loyaltyFeatureItems.length > 0 && <FilterPanelFilterGroup loading={fetching} title={<LoyaltyTierLabel kind="primary">{getLoyaltyProgramDisplayName()}</LoyaltyTierLabel>}>
          <FilterPanelCheckboxGroup
            name="loyaltyFeatures"
            items={loyaltyFeatureItems}
            onChange={onLoyaltyFeatureChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {enabledFilters.agentHubFeatures && agentHubFeatureItems.length > 0 && <FilterPanelFilterGroup loading={fetching} title={agentHubFeaturesTitle}>
          <FilterPanelCheckboxGroup
            name="agentHubFeatures"
            items={agentHubFeatureItems}
            maxItems={5}
            onChange={onAgentHubFeatureChange}
          />
        </FilterPanelFilterGroup>}
        {enabledFilters.customerRating && isNonEmptyArray(customerRatingItems) && <FilterPanelFilterGroup loading={fetching} title={customerRatingTitle}>
          <FilterPanelCheckboxGroup
            name="customerRating"
            items={customerRatingItems}
            onChange={onCustomerRatingChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {hasRoomBathOrBedFilters && !bedroomsFilterGroupAtBottom && !showRoomsAndAmenitiesAtTop && (
          <RoomFilterGroup
            loading={fetching}
            filters={filters}
            enabledFilters={enabledFilters}
            onBedroomsChange={debouncedBedroomsChange}
            onBedsChange={debouncedBedsChange}
            onBathroomsChange={debouncedBathroomsChange}
          />
        )}
        {enabledFilters.holidayTypes && isNonEmptyArray(holidayTypeItems) && <FilterPanelFilterGroup loading={fetching} title={holidayTypeTitle}>
          <FilterPanelCheckboxGroup
            name="holidayTypes"
            items={holidayTypeItems}
            onChange={onHolidayTypeChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {enabledFilters.inclusions && isNonEmptyArray(Object.entries(inclusionItems)) &&
          <FilterPanelFilterGroup loading={fetching} title={inclusionsTitle}>
            <VerticalSpacer gap={24}>
              {Object.entries(inclusionItems).map(([category, items]) => (
                <div key={category}>
                  <BodyText variant="medium" weight="bold">{category}</BodyText>
                  <FilterPanelCheckboxGroup
                    key={category}
                    name="inclusions"
                    items={items}
                    onChange={onInclusionsChange}
                    maxItems={5}
                  />
                </div>
              ))}
            </VerticalSpacer>
          </FilterPanelFilterGroup>}
        {!shouldShowTopRoomsAndAmenities && enabledFilters.amenities && isNonEmptyArray(amenityItems) && <FilterPanelFilterGroup loading={fetching} title={amenitiesTitle}>
          <FilterPanelCheckboxGroup
            name="amenities"
            items={amenityItems}
            onChange={onAmenityChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {enabledFilters.paymentOptions && isNonEmptyArray(paymentOptionItems) && <FilterPanelFilterGroup loading={fetching} title={paymentOptionsTitle}>
          <FilterPanelCheckboxGroup
            name="paymentOptions"
            items={paymentOptionItems}
            onChange={onPaymentOptionChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {enabledFilters.chains && isNonEmptyArray(chainItems) && <FilterPanelFilterGroup loading={fetching} title={propertyBrandsTitle}>
          <FilterPanelCheckboxGroup
            name="chains"
            items={chainItems}
            onChange={onChainChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {hasRoomBathOrBedFilters && bedroomsFilterGroupAtBottom && (
          <RoomFilterGroup
            loading={fetching}
            filters={filters}
            enabledFilters={enabledFilters}
            onBedroomsChange={debouncedBedroomsChange}
            onBedsChange={debouncedBedsChange}
            onBathroomsChange={debouncedBathroomsChange}
          />
        )}
      </ScrollableContent>
    </FilterPaneContainer>
  ) }

export default connect<MappedStateProps, undefined, Props, App.State>(
  (appState): MappedStateProps => {
    return {
      popularFilters: appState.destination.popularFilters,
      fetchingPopularFilters: appState.destination.fetchingPopularFilters,
      pathname: appState.router.location.pathname,
      currentHash: appState.router.location.hash,
      canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(appState),
      userId: appState.auth.account.memberId,
      isLoggedIn: selectLoggedIn(appState),
    }
  },
)(HotelSearchPageFilterPane)
